import React from 'react';
import { flowRight } from 'lodash';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import AiChatBaseTemplate from '../../components/ai-chat-base-template/AiChatBaseTemplate.component';
import { fetchFlashcardLibraryAction } from '../../components/app/app-state-actions.utilities';
import { ELSIcon } from '../../components/els.components';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlashcardsPanel } from '../../components/flashcards-panel/FlashcardsPanel.component';
import {
  FlashcardListItemDto,
  FlashcardSelectionDto,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import { ELSButtonSize, ELSButtonType } from '../../models/button.models';
import { RecordEmpty } from '../../models/common.models';
import { convertMarkdownToPlainText } from '../../utilities/common.utilities';
import { isShowFlashcardsNav } from '../ai-chat/ai-chat.utilities';
import { LibraryPageContent, LibraryPageLabels } from '../library-page/LibraryPageContent.component';
import { useLibraryPage } from '../library-page/useLibraryPage.hook';
import { FLASHCARDS_PAGE_SIZE } from './flashcards.constants';

const FLASHCARDS_LABELS: LibraryPageLabels = {
  block: 'c-naa-flashcards',
  fieldName: 'flashcards',
  title: 'Flashcards',
  item: 'Flashcard',
  column: 'Flashcard',
  items: 'flashcards',
  menuId: 'flashcard-menu-tooltip',
  paginatorId: 'flashcards-paginator',
};

// The player renders the card as markdown, so the row shows the front as plain text
const getFrontText = (flashcard: FlashcardListItemDto): string => convertMarkdownToPlainText(flashcard.frontText);

export type FlashcardsProps = ELSPropsFromToastService;

export const FlashcardsComponent = (props: FlashcardsProps): JSX.Element => {

  // The panel opens with the selection View was clicked for, fresh and in the list's sort order every time
  const library = useLibraryPage<FlashcardListItemDto, FlashcardSelectionDto>({
    fetchList: fetchFlashcardLibraryAction,
    pageSize: FLASHCARDS_PAGE_SIZE,
    isShowNav: isShowFlashcardsNav,
    getRowText: getFrontText,
    toastService: props.toastService,
  });
  return (
    <AiChatBaseTemplate
      isPanelOpen={!!library.panelContent}
      panelContent={library.panelContent && (
        <div className="c-naa-panel">
          <FlashcardsPanel
            key={library.panelKey}
            selection={library.panelContent}
            onError={library.handlePanelError}
            headerEnd={(
              <button type="button" className="u-els-anchorize" onClick={library.closePanel}>
                <ELSIcon name="close" size="1x" />
                <span className="u-els-hide-visually">Close flashcards</span>
              </button>
            )}
          />
        </div>
      )}
    >
      <LibraryPageContent
        library={library}
        labels={FLASHCARDS_LABELS}
        actions={(
          <FlexItem>
            <ELSButton
              preventAutoPending
              type={ELSButtonType.PRIMARY}
              size={ELSButtonSize.SMALL}
              isDisabled={!library.itemCount}
              onClick={() => library.openPanel(library.selection)}
            >
              {`View flashcards (${library.itemCount})`}
            </ELSButton>
          </FlexItem>
        )}
      />
    </AiChatBaseTemplate>
  );
};

const enhance = flowRight(
  ELSWithToastService,
  withHTMLHeadSEO({ title: 'Flashcards' }),
);

const Flashcards: React.FC<RecordEmpty> = enhance(FlashcardsComponent);

export default Flashcards;
